import type { ConnectionPositionPair } from '@angular/cdk/overlay';
import { Component, computed, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';

import type { User } from '@recruitee/account-types';
import { AnalyticsMode, NotificationsAnalytics } from '@recruitee/analytics';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import {
  AdminsAvatarsComponent,
  AvatarComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  ConnectedOverlayDirective,
  DropdownComponent,
  DropdownItemComponent,
  GroupComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  OverlayOriginDirective,
  RtTooltip,
} from '@recruitee/user-interface';

import { FollowersCounterComponent } from '../followers-counter/followers-counter.component';

@Component({
  selector: 'rt-candidate-follow-button',
  imports: [
    GroupComponent,
    IconComponent,
    OverlayOriginDirective,
    ButtonComponent,
    RtTooltip,
    ButtonLoadingDirective,
    TranslatePipe,
    ConnectedOverlayDirective,
    DropdownComponent,
    DropdownItemComponent,
    HeadingComponent,
    AdminsAvatarsComponent,
    AvatarComponent,
    IconButtonComponent,
    FollowersCounterComponent,
    RouterLink,
  ],
  templateUrl: './candidate-follow-button.component.html',
  styleUrl: './candidate-follow-button.component.less',
  providers: [NotificationsAnalytics],
})
export class CandidateFollowButtonComponent {
  public followed = input<boolean>();
  public loading = input<boolean>();
  public users = input<User[]>();
  public canManageCandidates = input<boolean>();
  public canManageHiredCandidates = input<boolean>();
  public isCandidateHired = input<boolean>();

  public canManageFollowers = computed(() =>
    this.isCandidateHired() ? this.canManageHiredCandidates() : this.canManageCandidates(),
  );

  public statusText = computed(() =>
    this.followed()
      ? this.i18n.translate('app.follow_dropdown.following')
      : this.i18n.translate('app.follow_dropdown.follow'),
  );

  public headingText = computed(() =>
    this.followed()
      ? this.i18n.translate('app.follow_dropdown.following')
      : this.i18n.translate('app.follow_dropdown.manage_followers.title'),
  );

  public descriptionText = computed(() =>
    this.followed()
      ? this.i18n.translate('app.follow_dropdown.manage_followers.description_followed')
      : this.i18n.translate('app.follow_dropdown.manage_followers.description'),
  );

  public tooltipText = computed(() =>
    this.users()?.length
      ? this.i18n.translate('app.follow_dropdown.manage_followers.x_team_members_follows', {
          count: this.users()!.length,
        })
      : this.i18n.translate('app.follow_dropdown.follow_btn_tooltip_candidate_v2'),
  );

  public followClick = output();
  public unfollowClick = output();
  public addFollowersClick = output();
  public removeFollowerClick = output<User>();

  public dropdownPositions: ConnectionPositionPair[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
  ];

  constructor(
    private analytics: NotificationsAnalytics,
    private i18n: I18nService,
  ) {}

  public followAction(): void {
    this.followClick.emit();
    this.analytics.trackFollowAction('candidate', AnalyticsMode.Single);
  }

  public unfollowAction(): void {
    this.unfollowClick.emit();
    this.analytics.trackUnfollowAction('candidate', AnalyticsMode.Single);
  }

  public addFollowersAction(): void {
    this.addFollowersClick.emit();
  }
}
